<template>
  <div class="card modal-overflow edit-job" style="box-shadow: none;">
    <div class="card-body new-job">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>Job Name</label>
            <div class="job-name-container grey-font">
              <h6>
                {{ edit.name }}
                <span v-if="edit.actualCompletionDate">
                  - Completed on: {{ edit.actualCompletionDate | formatDate }}
                </span>
              </h6>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Job Number
              <el-tooltip effect="dark" content="Click the ' + ' button to auto-generate a job number with the pattern (yyyy-mm--xxx). Alternatively, you can enter any value you choose." placement="top">
                <img class="icon-help" src="static/img/icon-help.png"/>
              </el-tooltip>
            </label>
            <button @click="generateJobNumber" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <el-input
              size="large"
              name="jobNumber"
              clearable
              :disabled="!canEdit"
              class="job-number"
              v-model="edit.number"
              @blur="ifJobNumberExistsThenWarn"
              placeholder="Job Number">
            </el-input>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Permit Number
            </label>
            <el-input type="input"
                      name="permitNumber"
                      placeholder="Permit Number"
                      v-model="edit.permitNumber"
                      clearable
                      :disabled="!canEdit" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Coordinator</label>
            <el-autocomplete
              style="width:100%"
              data-vv-as="job coordinator"
              size="large"
              ref="coordinatorId"
              name="coordinatorId"
              class="inline-input"
              clearable
              :disabled="!canEdit"
              v-validate="modelValidations.coordinatorId"
              v-model="selectedCoordinator"
              :fetch-suggestions="findCoordinators"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Job Coordinator"
              @blur="verifyCoordinatorAutocompleteSelected"
              @select="setCoordinator">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('coordinatorId')">
              {{ getError('coordinatorId') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('coordinatorId')">
              {{ fieldNotValidError('Coordinator') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Priority</label>
            <el-select class="select"
                       name="priority"
                       size="large"
                       clearable
                       :disabled="!canEdit"
                       placeholder="Job Priority"
                       v-validate="modelValidations.priority"
                       v-model="edit.priority">
              <el-option v-for="option in priorities"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('priority')">
              {{ getError('priority') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Status</label>
            <el-select class="select"
                       name="status"
                       size="large"
                       @change="handleCompletedOrCancelled"
                       :disabled="!canEdit"
                       placeholder="Job Status"
                       v-validate="modelValidations.status"
                       v-model="edit.status">
              <el-option v-for="option in statuses"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('status')">
              {{ getError('status') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Elevation</label>
            <el-input type="input"
                      name="elevation"
                      placeholder="Elevation"
                      v-model="edit.elevation"
                      clearable
                      :disabled="!canEdit" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <label>Billable Client</label>
            <button v-if="isClientResult && canEdit" @click="copyClientToBuilder" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> > </button>
            <span v-if="hasAdminNotes">
              <el-tooltip effect="dark" placement="top" style="padding: 1px 5px; margin-top: -1px; float: right;">
                <div slot="content">
                  <h6 style="text-align: center">Notes</h6>
                  <p v-html="adminNotes"></p>
                </div>
                <img class="icon-help m-left-4" src="static/img/icon-info.png" />
              </el-tooltip>
            </span>
            <span v-if="hasTravelCharge">
              <el-tooltip effect="dark" placement="top" style="padding: 1px 0; margin-top: -1px; float: right;">
                <div slot="content">
                  <h6 style="text-align: center">Travel Charge</h6>
                  <p style="text-align: center">{{currentFormatter.format(travelCharge.value)}}</p>
                  <span v-if=isMultiplePriceRules><p>Multiple price rules matched</p></span>
                </div>
                <i class="nc-icon nc-money-coins"></i>`
              </el-tooltip>
            </span>
            <el-autocomplete
              ref="clientId"
              style="width:100%"
              name="clientId"
              class="inline-input"
              :debounce="100"
              clearable
              :disabled="!canEdit"
              v-model="selectedClient.name"
              :fetch-suggestions="findClients"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Billable Client"
              @blur="verifyClientAutocompleteSelected"
              @select="setClient">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('clientId')">
              {{ getError('clientId') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
              {{ fieldNotValidError('Client') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Client Contact</label>
            <button v-if="isClientResult && canEdit" @click="addClientContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <button v-if="isClientResult && edit.clientContactId && canEdit" @click="editClientContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <span v-if="hasClientContact">
              <el-tooltip effect="dark" placement="top" style="padding: 1px 5px; margin-top: -1px; float: right;">
                <div slot="content">
                  <h6 style="text-align: center">Client Contact</h6>
                  <p v-html="clientContactDisplay"></p>
                </div>
                <img class="icon-help m-left-4" src="static/img/icon-info.png" />
              </el-tooltip>
            </span>
            <v-select
                v-model="selectedClientContact"
                @input="setClientContact"
                placeholder="Client Contact"
                :disabled="!canEdit"
                label="label"
                value="value"
                key="label"
                :options="clientContacts">
            </v-select>
            <small class="text-danger" v-show="errors.has('clientContactId')">
              {{ getError('clientContactId') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Builder</label>
            <button v-if="isBuilderResult && canEdit" @click="copyBuilderToClient" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> < </button>
            <el-autocomplete
              ref="builderId"
              style="width:100%"
              name="builderId"
              class="inline-input"
              :debounce="150"
              clearable
              :disabled="!canEdit"
              v-model="selectedBuilder.name"
              :fetch-suggestions="findBuilders"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Builder"
              @blur="verifyBuilderAutocompleteSelected"
              @select="setBuilder">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('builderId')">
              {{ getError('builderId') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
              {{ fieldNotValidError('Builder') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Builder Contact</label>
            <button v-if="isBuilderResult && canEdit" @click="addBuilderContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <button v-if="isBuilderResult && edit.builderContactId && canEdit" @click="editBuilderContact" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <v-select
                v-model="selectedBuilderContact"
                @input="setBuilderContact"
                placeholder="Builder Contact"
                :disabled="!canEdit"
                label="label"
                value="value"
                key="label"
                :options="builderContacts">
            </v-select>
            <small class="text-danger" v-show="errors.has('builderContactId')">
              {{ getError('builderContactId') }}
            </small>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <div class="form-group">
            <label>Community</label>
            <el-autocomplete
              style="width:100%"
              ref="community"
              name="community"
              class="inline-input"
              clearable
              :disabled="!canEdit"
              v-model="selectedCommunity.name"
              :fetch-suggestions="findCommunities"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Community"
              @blur="verifyCommunityAutocompleteSelected"
              @focus="verifyClientOrBuilderSelected"
              @select="setCommunity">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('community')">
              {{ fieldNotValidError('Community') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Plan Name</label>
            <span v-if="selectedPlanName && planNamePDF">
              <el-tooltip class="item" effect="dark" placement="right" style="padding: 1px 5px; margin-top: -2px; float: right;">
                <div slot="content" style="text-align: center"> {{ planNamePDF }} </div>
                <img src="static/img/file-mgr/pdf-small.png"/>
              </el-tooltip>
            </span>
            <button v-if="selectedPlanName && canEdit" @click="editPlanName" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> edit </button>
            <button v-if="!selectedPlanName && canEdit" @click="addPlanName" style="margin-bottom: 0;" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
            <el-autocomplete
              style="width:100%"
              size="large"
              name="planNameId"
              ref="planNameId"
              class="inline-input"
              clearable
              :disabled="!canEdit"
              v-model="selectedPlanName"
              :fetch-suggestions="findPlanNames"
              :trigger-on-focus="false"
              value-key="plan"
              placeholder="Plan Name"
              @blur="verifyPlanNameAutocompleteSelected"
              @focus="verifyBuilderSelected"
              @select="setPlanName">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('planNameId')">
              {{ fieldNotValidError('Plan Name') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Date Received</label>
            <el-date-picker
              :disabled="!canEdit"
              name="dateReceived"
              data-vv-as="date received"
              :format="constants.DATETIME.END_OF_DAY.DISPLAY"
              :value-format="constants.DATETIME.END_OF_DAY.VALUE"
              v-validate="modelValidations.dateReceived"
              v-model="edit.dateReceived"
              type="date"
              @change="setDateReceived"
              :picker-options="datePickerConfig"
              placeholder="Pick a day">
            </el-date-picker>
            <small class="text-danger" v-show="errors.has('dateReceived')">
              {{ getError('dateReceived') }}
            </small>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Target Completion Date</label>
            <el-date-picker
              :disabled="!canEdit"
              name="targetCompletionDate"
              data-vv-as="target completion date"
              :format="constants.DATETIME.END_OF_DAY.DISPLAY"
              :value-format="constants.DATETIME.END_OF_DAY.VALUE"
              v-validate="modelValidations.targetCompletionDate"
              v-model="targetCompletionDate"
              type="date"
              @change="setTargetCompletionDateAndPromptToChangeTaskDueDates"
              :picker-options="datePickerConfig"
              placeholder="Pick a day">
            </el-date-picker>
            <small class="text-danger" v-show="errors.has('targetCompletionDate')">
              {{ getError('targetCompletionDate') }}
            </small>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-9">
          <div class="form-group">
            <label>Description</label>
            <ckeditor :editor="editor" :disabled="!canEditNotes" v-model="localDescription" :config="ckEditorConfig"></ckeditor>
          </div>
          <span v-if="projectName">
            Project: <a @click.prevent="closeModalAndGoToProject" href="#">{{projectName}}</a>
          </span>
        </div>
        <div class="col-md-3">
          <auditable-entity :data="edit"></auditable-entity>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <button :disabled="isSubmitDisabled" @click="checkClientAndPoRequiredAndSave" class="btn btn-fill btn-info btn-wd btn-save pull-right" v-if="canEdit || canEditNotes">Save Job</button>
            <button @click="saveAsJobTemplate" class="btn btn-fill btn-info btn-wd btn-save pull-right" v-if="canEditJobTemplate">Save as Job Template</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {
  assemblePlanNameDisplay,
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  findCommunities,
  findCompany,
  notifyError,
  notifySuccess,
  objectBy,
  verifyAutocompleteSelected,
  utcToLocalForPatch,
  dateNoon
} from 'src/utilities/utils'
import {mapGetters} from 'vuex'
import PSwitch from 'src/components/UIComponents/Switch'
import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import ckEditorConfig from 'src/utilities/ckeditor'
import datePickerConfig from 'src/components/UIComponents/DatePicker/datePickerConfig'
import {OK} from 'http-status-codes'
import _ from 'lodash'
import {sync} from 'vuex-pathify'
import {
  jobAddBuilderContact,
  jobAddClientContact,
  jobCopyBuilderToClient,
  jobCopyClientToBuilder,
  jobEditBuilderContact,
  jobEditClientContact,
  jobFetchTravelCharge,
  jobFindBuilders,
  jobFindClients,
  jobFindCoordinators,
  jobFindPlanNames,
  jobAddPlanName,
  jobEditPlanName,
  jobFetchPlanNamePDF,
  jobGenerateJobNumber,
  jobIfJobNumberExistsThenWarn,
  jobMaybeLoadBuilder,
  jobMaybeLoadClient,
  jobMaybeLoadCoordinator,
  jobNoCompanySelected,
  jobSetBuilder,
  jobSetClient,
  jobSetCommunity,
  jobSetCompany,
  jobSetCoordinator,
  jobSetPlanName,
  jobVerifyBuilderSelected,
  jobVerifyClientOrBuilderSelected,
  jobWarnWhenCompletedOrCancelled,
  jobValidateContactEmails,
  communityExistsButNotAssociated
} from 'src/utilities/jobHelper'
import vSelect from 'vue-select'

export default {
    props: ['data'],
    components: {
      AuditableEntity,
      PSwitch,
      ckeditor: CKEditor.component,
      vSelect
    },
    computed: {
      edit: sync('jobs@edit'),
      ...mapGetters({
        username: 'security/name',
        users: 'users/kvpsForSelect',
        priorities: 'priorities/job',
        departments: 'departments/kvpsForSelect',
        contacts: 'companies/companyContacts',
        autocompleteCommunities: 'communities/results',
        activeModals: 'modalTracker/activeModals',
        autocompleteClients: 'companies/autocompleteResultsClient',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder'
      }),
      statuses() {
        let statuses = this.$store.getters['statuses/job']
        const existingStatus = this.$store.get('jobs@edit.status')
        // restrict cancelling job to admin and super user. Remove Cancelled option if not one of those two
        if (!this.canCancelJob) {
          return _.filter(statuses, (status) => status.value !== Constants.STATUSES.CANCELLED)
        }
        // if has job has been cancelled or completed, do not allow a further status change
        if (existingStatus === Constants.STATUSES.CANCELLED
          || existingStatus === Constants.STATUSES.COMPLETED) {
          return _.filter(statuses, (status) => status.value === existingStatus)
        }
        return statuses
      },
      canRead() {
        return this.$can('read', Permissions.JOBS)
      },
      canEdit() {
        if (this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)
          || this.$store.get('security/isInGroup', Constants.GROUPS.ADMIN_USERS)) {
          return true
        }
        if (this.$store.get('jobs@edit.actualCompletionDate')) {
          return false
        }
        return this.$can('edit', Permissions.JOBS)
      },
      canEditNotes() {
        return this.$can('edit', Permissions.JOBS)
      },
      canEditTaskDueDates() {
        return this.$can('edit', Permissions.TASK_DUE_DATE)
      },
      canEditJobTemplate() {
        return this.$can('edit', Permissions.JOB_TEMPLATES)
      },
      canCancelJob() {
        return this.$can('edit', Permissions.CANCEL_JOBS)
      },
      hasTravelCharge() {
        return this.travelCharge.value !== null
      },
      isMultiplePriceRules() {
        return this.travelCharge.state === Constants.TRAVEL_CHARGE.STATES.UNDECIDED
      },
      hasAdminNotes() {
        return typeof(this.adminNotes) === 'string' && this.adminNotes.trim()
      },
      hasClientContact() {
        return !!this.selectedClientContact
      },
      clientContactDisplay() {
        let contactDisplay = "<p>"

        if (this.selectedClientContact) {
          if (this.selectedClientContact.contact.title) {
            contactDisplay += `Title: ${this.selectedClientContact.contact.title}<br>`
          }
          if (this.selectedClientContact.contact.name) {
            contactDisplay += `Name: ${this.selectedClientContact.contact.name}<br>`
          }
          if (this.selectedClientContact.contact.mobilePhone) {
            contactDisplay += `Mobile Phone: ${this.selectedClientContact.contact.mobilePhone}<br>`
          }
          if (this.selectedClientContact.contact.workPhone) {
            contactDisplay += `Work Phone: ${this.selectedClientContact.contact.workPhone}<br>`
          }
          this.selectedClientContact.contact.emails.forEach((email, index) => contactDisplay += `Email${index+1}: ${email}<br>`)
        }
        contactDisplay += "<p>"

        return contactDisplay
      },
      isSubmitDisabled() {
        return this.isProcessing
      }
    },
    data() {
      return {
        editor: ClassicEditor,
        ckEditorConfig,
        datePickerConfig,
        initialJobStatus: null,
        localDescription: null,
        selectedPlanName: null,
        planNamePDF: null,
        selectedCoordinator: null,
        selectedCommunity: { id: null, name: null },
        selectedClient: { id: null, name: null },
        selectedBuilder: { id: null, name: null },
        selectedClientContact: null,
        selectedBuilderContact: null,
        isClientResult: false,
        isBuilderResult: false,
        isAddClientContact: false,
        isAddBuilderContact: false,
        targetCompletionDate: null,
        isProcessing: false,
        clientContacts: [],
        builderContacts: [],
        hasCancelled: false,
        onHoldWarning: false,
        constants: Constants,
        adminNotes: '',
        projectName: '',
        tasks: [],
        modelValidations: {
          coordinatorId: {
            required: true
          },
          priority: {
            required: true
          },
          status: {
            required: true
          },
          dateReceived: {
            required: true
          },
          targetCompletionDate: {
            required: true
          }
        },
        newCompany: {
          builder: false,
          client: false
        },
        travelCharge: {
          state: null,
          value: null
        },
        currentFormatter : Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        })
      }
    },
    methods: {
      addCancelledDescription() {
        this.localDescription += `This job was ${this.edit.status} by ${this.username} on ${new Date()}`
      },
      confirmedCompleteOrCancel() {
        this.hasCancelled = true
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`jobs/edit@${refAndPathName}`))
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      validate() {
        return this.$validator.validateAll()
      },
      closeModalAndGoToProject() {
        this.$emit('close')
        if (this.$route.path !== `/projects/${this.edit.projectId}`) {
          this.$router.push({path: `/projects/${this.edit.projectId}`})
        }
      },
      async handleCompletedOrCancelled(jobStatus) {
        // get tasks by jobId and check for pending invoice tasks
        if (jobStatus === Constants.STATUSES.CANCELLED || jobStatus === Constants.STATUSES.COMPLETED) {
          const tasksResponse = await this.$store.dispatch('tasks/fetchTasksByJobId', this.data.id)
          if (OK !== tasksResponse.status) {
            notifyError(this, 'Failed to retrieve PTRAC tasks for job ID ' + this.data.jobId)
            return
          }
          let hasPendingInvoice = false
          tasksResponse.data.forEach(task => {
            if ((task.name === Constants.TASKS.NAMES.INVOICE_SUBMITTAL || task.name === Constants.TASKS.NAMES.INVOICE)
              && task.status === Constants.STATUSES.PENDING) {
              hasPendingInvoice = true
            }
          })
          jobWarnWhenCompletedOrCancelled(this, jobStatus, hasPendingInvoice)
        }
      },
      setJobStatus(jobStatus) {
        this.$store.set('jobs/edit@status', jobStatus)
      },
      setDateReceived(dateReceived) {
        this.$store.set('jobs/edit@dateReceived', dateNoon(dateReceived))
      },
      setTargetCompletionDateAndPromptToChangeTaskDueDates(targetCompletionDate) {
        this.$store.set('jobs/edit@targetCompletionDate', dateNoon(targetCompletionDate))
        if (this.canEditTaskDueDates) {
          this.$eventHub.$emit('show-modal-2', 'Modify Task Due Dates', {jobId: this.data.id, dueDate: targetCompletionDate}, '500px')
        }
      },
      async checkClientAndPoRequiredAndSave() {
        if (!this.$store.get('jobs/edit@clientId')) {
          this.$eventHub.$emit('show-modal-2', 'No Client Selected', null, '450px')
          return
        }

        await this.validateContactEmails()
      },
      async validateContactEmails() {
        await jobValidateContactEmails(this)
      },
      async validateJobDetailsAndSubmit() {
        this.isProcessing = true

        if (this.hasCancelled) {
          this.addCancelledDescription()
        }
        if (await this.$validator.validateAll()) {
          const savePayload = this.$store.get('jobs/savePayload', 'jobs')
          savePayload.data.description = this.localDescription // ckeditor struggling with vuex state and nulls
          const response = await this.$store.dispatch('save', savePayload)

          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            notifySuccess(this, 'Job saved successfully')
            this.$eventHub.$emit('jobSaved', response.data)
            this.$eventHub.$emit('refreshProjectJobs', response.data)
            if (this.$store.get('jobs/updateTaskDueDates')) {
              await this.updateTaskDueDates()
            }
            this.$emit('close')
          } else {
            notifyError(this, 'Failed to Update Job', response.errors)
          }
        }
        this.isProcessing = false
      },
      saveAsJobTemplate() {
        const job = JSON.parse(JSON.stringify(this.$store.get('jobs@edit')))

        job.clientName = this.selectedClient.name
        job.clientContacts = this.clientContacts
        job.builderName = this.selectedBuilder.name
        job.builderContacts = this.builderContacts
        job.communityName = this.selectedCommunity.name

        this.$eventHub.$emit('show-modal-4', 'Job Template', {job: job}, '800px')
      },
      async updateTaskDueDates() {
        const responses = []
        for (const task of this.tasks) {
          if (task.status === Constants.STATUSES.COMPLETED || task.status === Constants.STATUSES.CANCELLED) {
            notifyError(this, task.name + ' cannot be edited as it is either cancelled or completed')
          } else {
            task.dueDate = this.edit.targetCompletionDate
            responses.push(await this.$store.dispatch('save2', {
              endpoint: 'tasks',
              data: task
            }))
          }
        }

        if (responses.length > 0) {
          responses.forEach((response) => {
            if (response.status === 200) {
              notifySuccess(this, response.data.name + ' due date updated successfully')
            } else {
              notifyError(this, 'Failed to update ' + response.data.name)
            }
          })
        } else {
        notifyError(this, 'Failed to update tasks.')
        }
      },
      async findPlanNames(text, cb) {
        await jobFindPlanNames(this, text, cb)
      },
      setPlanName(planName) {
        jobSetPlanName(this, planName)
        this.fetchPlanNamePDF()
      },
      async maybeLoadPlanName() {
        const planNameId = this.$store.get('jobs/edit@planNameId')
        if (planNameId) {
          const response = await this.$store.dispatch('fetchById', {endpoint: 'plan-names', id: planNameId})
          if (OK === response.status) {
            this.selectedPlanName = assemblePlanNameDisplay(response.data)
          }
        }
      },
      editPlanName() {
        jobEditPlanName(this)
      },
      addPlanName() {
        jobAddPlanName(this)
      },
      async fetchPlanNamePDF() {
        await jobFetchPlanNamePDF(this)
      },
      async findCommunities(text, cb) {
        await findCommunities(this, text, cb, 'jobs/edit@community')
      },
      setCommunity(community) {
        jobSetCommunity(this, community)
      },
      async findClients(text, cb) {
        await jobFindClients(this, text, cb)
      },
      async findBuilders(text, cb) {
        await jobFindBuilders(this, text, cb)
      },
      async findCompany(text, type) {
        return findCompany(this, text, type)
      },
      async addClientContact() {
        await jobAddClientContact(this)
      },
      async editClientContact() {
        await jobEditClientContact(this)
      },
      async addBuilderContact() {
        await jobAddBuilderContact(this)
      },
      async editBuilderContact() {
        await jobEditBuilderContact(this)
      },
      findCoordinators(text, cb) {
        jobFindCoordinators(this, text, cb)
      },
      setCoordinator(coordinator) {
        jobSetCoordinator(this, coordinator)
      },
      async maybeLoadClient() {
        await jobMaybeLoadClient(this)
      },
      setCompany(company) {
        jobSetCompany(this, company)
      },
      async setClient(client) {
        this.adminNotes = _.get(client, `notesByDepartment.${this.edit.departmentId}`)

        jobSetClient(this, client)
        this.setClientContactFromId()
        if (this.edit.status !== Constants.STATUSES.COMPLETED && this.edit.status !== Constants.STATUSES.CANCELLED) {
          await jobFetchTravelCharge(this)
        }
      },
      async maybeLoadBuilder() {
        await jobMaybeLoadBuilder(this)
      },
      setBuilder(builder) {
        jobSetBuilder(this, builder)
        this.setBuilderContactFromId()
      },
      verifyCoordinatorAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobs/edit@coordinatorId', 'coordinator', this.selectedCoordinator)
      },
      verifyClientAutocompleteSelected() {
        if (!this.newCompany.client && _.get(this, 'selectedClient.name')) {
          verifyAutocompleteSelected(this, 'jobs/edit@clientId', 'client', this.selectedClient.name)
        }
      },
      verifyBuilderAutocompleteSelected() {
        if (!this.newCompany.builder && _.get(this, 'selectedBuilder.name')) {
          verifyAutocompleteSelected(this, 'jobs/edit@builderId', 'builder', this.selectedBuilder.name)
        }
      },
      verifyCommunityAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobs/edit@community.id', 'community', this.selectedCommunity.name)
      },
      verifyPlanNameAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'jobs/edit@planNameId', 'plan name', this.selectedPlanName)
      },
      noCompanySelected(type) {
        jobNoCompanySelected(this, type)
      },
      noCommunitySelected() {
        this.selectedCommunity = { id: null, name: null }
      },
      noPlanNameSelected() {
        this.selectedPlanName = null
      },
      maybeLoadCommunity() {
        const community = this.$store.get('jobs/edit@community')
        if (community) {
          this.selectedCommunity = community
        }
      },
      maybeLoadCoordinator() {
        jobMaybeLoadCoordinator(this)
      },
      verifyBuilderSelected() {
        return jobVerifyBuilderSelected(this)
      },
      verifyClientOrBuilderSelected() {
        return jobVerifyClientOrBuilderSelected(this)
      },
      // called when adding a new contact on the fly
      async maybeLoadClientAndBuilder(contact) {
        await Promise.all([
          this.maybeLoadClient(),
          this.maybeLoadBuilder()
        ])
        this.maybeSetClientContact(contact)
        this.maybeSetBuilderContact(contact)
      },
      maybeSetClientContact(contact) {
        const foundContact = objectBy(this.clientContacts, 'value', contact.id)
        if (foundContact && this.isAddClientContact) {
          this.selectedClientContact = foundContact
          this.$store.set('jobs/edit@clientContactId', contact.id)
        }
        this.isAddClientContact = false
      },
      setClientContact(clientContact) {
        if (!clientContact) {
          this.$store.set('jobs/edit@clientContactId', null)
          return
        }
        this.$store.set('jobs/edit@clientContactId', clientContact.value)
        this.selectedClientContact = clientContact
      },
      maybeSetBuilderContact(contact) {
        const foundContact = objectBy(this.builderContacts, 'value', contact.id)
        if (foundContact && this.isAddBuilderContact) {
          this.selectedBuilderContact = foundContact
          this.$store.set('jobs/edit@builderContactId', contact.id)
        }
        this.isAddBuilderContact = false
      },
      setBuilderContact(builderContact) {
        if (!builderContact) {
          this.$store.set('jobs/edit@builderContactId', null)
          return
        }
        this.$store.set('jobs/edit@builderContactId', builderContact.value)
        this.selectedBuilderContact = builderContact
      },
      async maybeLoadAutoCompletes() {
        await Promise.all([
          this.maybeLoadPlanName(),
          this.maybeLoadClient(),
          this.maybeLoadBuilder()
        ])
        this.maybeLoadCommunity()
        this.maybeLoadCoordinator()
      },
      async generateJobNumber() {
        await jobGenerateJobNumber(this)
      },
      resetCompanyContactBooleans() {
        this.isAddClientContact = false
        this.isAddBuilderContact = false
      },
      async ifJobNumberExistsThenWarn() {
        await jobIfJobNumberExistsThenWarn(this)
      },
      async copyClientToBuilder() {
        await jobCopyClientToBuilder(this)
        this.setBuilderContactFromId()
      },
      async copyBuilderToClient() {
        await jobCopyBuilderToClient(this)
        this.setClientContactFromId()
      },
      setBuilderContactFromId() {
        if (this.$store.get('jobs/edit@builderContactId')) {
          const builderContact = objectBy(this.builderContacts, 'value', this.$store.get('jobs/edit@builderContactId'))
          this.setBuilderContact(builderContact)
        }
      },
      setClientContactFromId() {
        if (this.$store.get('jobs/edit@clientContactId')) {
          const clientContact = objectBy(this.clientContacts, 'value', this.$store.get('jobs/edit@clientContactId'))
          this.setClientContact(clientContact)
        }
      },
      async fetchProject() {
        const response = await this.$store.dispatch('fetchById', {endpoint: 'projects', id: this.edit.projectId})

        if (OK === response.status) {
          this.projectName = response.data.name
        }
      },
      async fetchTasks() {
        const tasksResponse = await this.$store.dispatch('tasks/fetchTasksByJobId', this.data.id)
        if (OK === tasksResponse.status) {
          const tasks = tasksResponse.data
          tasks.forEach(task => {
            // dates coming from backend are in UTC
            if (task.dueDate) {
              task.dueDate = utcToLocalForPatch(task.dueDate)
            }
          })
          this.tasks = tasks
        }
      },
      async maybeResetCommunity() {
        if (!this.edit.clientId && !this.edit.builderId) {
          this.selectedCommunity = { id: null, name: null }
          return
        }

        const response = await this.$store.dispatch('fetchById', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMMUNITIES,
          id: this.edit.community.id
        })

        if (OK !== response.status) {
          this.selectedCommunity = { id: null, name: null }
          return
        }

        communityExistsButNotAssociated(this, response.data, this.edit.clientId, this.edit.builderId)
      }
    },
    async mounted() {
      await Promise.all([
        this.$store.cache.dispatch('users/fetchAll'),
        this.$store.cache.dispatch('statuses/fetchByEntityType', 'Job'),
        this.$store.cache.dispatch('priorities/fetchByEntityType', 'Job')
      ])

      const response = await this.$store.dispatch('fetchById', {endpoint: 'jobs', id: this.data.id})

      if (OK !== response.status) {
        notifyError(this, 'Failed to load Job by Id: ' + this.data.id)
        return
      }

      const job = response.data
      this.targetCompletionDate = job.targetCompletionDate

      this.$store.set('jobs/edit', job)

      this.initialJobStatus = response.data.status
      this.localDescription = this.$store.get('jobs/edit@description')
      await this.maybeLoadAutoCompletes()
      this.setBuilderContactFromId()
      this.setClientContactFromId()
      await this.fetchPlanNamePDF()
      await jobFetchTravelCharge(this)
      await this.fetchProject()
      await this.fetchTasks()

      if (this.selectedClient && this.selectedClient.id) {
        this.adminNotes = _.get(this.selectedClient, `notesByDepartment.${this.edit.departmentId}`)
      }

      this.$eventHub.$on('companySaved', this.setCompany)
      this.$eventHub.$on('noCompanySelected', this.noCompanySelected)
      this.$eventHub.$on('communitySaved', this.setCommunity)
      this.$eventHub.$on('noCommunitySelected', this.noCommunitySelected)
      this.$eventHub.$on('planNameSaved', this.setPlanName)
      this.$eventHub.$on('noPlanNameSelected', this.noPlanNameSelected)
      this.$eventHub.$on('companyContactUpdated', this.maybeLoadClientAndBuilder)
      this.$eventHub.$on('noCompanyContactSelected', this.resetCompanyContactBooleans)
      this.$eventHub.$on('resetJobStatus', this.setJobStatus)
      this.$eventHub.$on('confirmedCompleteOrCancel', this.confirmedCompleteOrCancel)
      this.$eventHub.$on('jobClientOrPoChecksComplete', this.validateContactEmails)
      this.$eventHub.$on('doNotAddContactEmail', this.validateJobDetailsAndSubmit)
    },
    beforeDestroy() {
      this.$store.commit('jobs/resetEdit')
      this.$store.set('jobs/updateTaskDueDates', false)

      this.$eventHub.$off('companySaved')
      this.$eventHub.$off('noCompanySelected')
      this.$eventHub.$off('communitySaved')
      this.$eventHub.$off('noCommunitySelected')
      this.$eventHub.$off('planNameSaved')
      this.$eventHub.$off('noPlanNameSelected')
      this.$eventHub.$off('companyContactUpdated')
      this.$eventHub.$off('noCompanyContactSelected')
      this.$eventHub.$off('resetJobStatus')
      this.$eventHub.$off('confirmedCompleteOrCancel')
      this.$eventHub.$off('jobClientOrPoChecksComplete')
      this.$eventHub.$off('doNotAddContactEmail')
    },
    watch: {
      selectedPlanName: {
        handler: function (selectedPlanName) {
          if (!selectedPlanName) {
            this.$store.commit('planNames/results', [])
            this.$store.set('jobs/edit@planNameId', null)
            this.planNamePDF = null
          }
        }
      },
      'selectedBuilder.name': {
        handler: function (selectedBuilderName) {
          if (!selectedBuilderName) {
            this.isBuilderResult = false
            this.$store.commit('companies/autocompleteResultsBuilder', [])
            this.$store.set('jobs/edit@builderId', null)
            this.$store.set('jobs/edit@builderContactId', null)
            this.selectedBuilderContact = null
            this.builderContacts = []
            this.maybeResetCommunity()
          }
        }
      },
      'selectedClient.name': {
        handler: function (selectedClientName) {
          if (!selectedClientName) {
            this.isClientResult = false
            this.$store.commit('companies/autocompleteResultsClient', [])
            this.$store.set('jobs/edit@clientId', null)
            this.$store.set('jobs/edit@clientContactId', null)
            this.selectedClientContact = null
            this.clientContacts = []
            this.$validator.errors.remove('clientId')
            this.travelCharge = { state: null, value: null }
            this.adminNotes = '' //reinitialize adminNotes if client unselected from "Billable Client" input field
            this.maybeResetCommunity()
          }
        }
      },
      selectedCoordinator: {
        handler: function (selectedCoordinator) {
          if (!selectedCoordinator) {
            this.$store.set('jobs/edit@coordinatorId', null)
          }
        }
      },
      'selectedCommunity.name': {
        handler: function (selectedCommunityName) {
          if (!selectedCommunityName) {
            this.$store.set('jobs/edit@community', {})
          }
        }
      }
    }
  }
</script>

<style scoped>
  .edit-job >>> #autocomplete-client .v-autocomplete-list, #autocomplete-builder .v-autocomplete-list {
    min-width: 280px;
    margin-top: 12px;
  }
  .edit-job >>> .job-name-container {
    background-color: lightgrey;
    padding: 11px 0 4px 8px;
    border-radius: 5px;
  }
  .edit-job >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
